import axios from 'axios';
import { getTenantFromSubdomain } from '../utils/tenant';

// Determine which API URL to use based on the flag
const useProduction = process.env.REACT_APP_USE_PRODUCTION === 'true';
const apiUrl = useProduction 
  ? process.env.REACT_APP_PROD_API_URL 
  : process.env.REACT_APP_DEV_API_URL;

const API = axios.create({
  baseURL: apiUrl,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Inject the multi-tenant identifier matching active browser subdomain.
  // Never override an X-Tenant-ID a caller set explicitly (e.g. super admin
  // performing a cross-tenant action from the admin subdomain).
  const explicitTenant = typeof config.headers?.get === 'function'
    ? config.headers.get('X-Tenant-ID')
    : config.headers?.['X-Tenant-ID'];
  const tenantSlug = getTenantFromSubdomain();
  if (tenantSlug && !explicitTenant) {
    config.headers['X-Tenant-ID'] = tenantSlug;
  }

  return config;
});

// Auto-logout safety net: if the access token expires (or is rejected) server-side,
// any authenticated request comes back 401. Clear the session and send the user to
// login. This complements the in-app expiry timer in AuthContext for cases where the
// timer can't fire (e.g. a long-backgrounded tab or clock skew).
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    // Don't treat the login/register/password flows as session expiry.
    const isAuthFlow = /\/auth\/(login|register|forgot-password|reset-password|update-password)/.test(url);

    if (status === 401 && localStorage.getItem('token') && !isAuthFlow) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('admin_access_token_backup');
      localStorage.removeItem('admin_user_backup');
      localStorage.removeItem('impersonation_target');
      if (window.location.pathname !== '/login') {
        window.location.replace('/login?expired=1');
      }
    }
    return Promise.reject(error);
  }
);

export default API;
